import { css, cx } from '@fable/theme'
import { useGSAP } from '@gsap/react'
import { gsap } from 'gsap'
import { useRef } from 'react'
import { GoalCompleteStar } from './GoalCompleteStar'
import { GoalCompleteStarGroupLeft } from './GoalCompleteStarGroupLeft'
import { GoalCompleteStarGroupRight } from './GoalCompleteStarGroupRight'
import { useStatsTheme } from '../../../theme/ctx'

interface Props {
  motion?: { delay?: number }
  stretched: boolean
}

export const ReadingGoalCompleteBg = ({ motion, stretched }: Props) => {
  const container = useRef<HTMLDivElement>(null)
  const theme = useStatsTheme((s) => s.theme)

  const shimmer = () => {
    const tl = gsap.timeline()

    tl.to('.anim__goal-complete-bg', {
      y: 0,
      duration: 1,
      ease: 'sine.out',
    })

    tl.to(
      '.anim__goal-complete-shimmer',
      {
        y: stretched ? '-275%' : '-200%',
        duration: 1.5,
        delay: 1.5,
        repeat: 4,
        yoyo: true,
        ease: 'sine.inOut',
      },
      '<50%'
    )

    return tl
  }

  const moveStars = () => {
    const tl = gsap.timeline({ delay: 1 })

    const stars = gsap.utils.toArray(
      '.anim__goal-complete-star'
    ) as HTMLElement[]

    tl.to(stars, {
      scale: 1,
      duration: 0.2,
      ease: 'sine.out',
      stagger: {
        each: 0.15,
      },
    })

    tl.to(stars[0], {
      y: 3,
      scale: 0.8,
      duration: 2,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
    })

    tl.to(
      stars[1],
      {
        y: -5,
        scale: 0.8,
        duration: 1,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      },
      '<'
    )

    tl.to(
      stars[2],
      {
        y: -2,
        duration: 2.5,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      },
      '<'
    )

    return tl
  }

  const moveStarGroups = () => {
    const tl = gsap.timeline({ delay: 0.5 })

    const stars = gsap.utils.toArray(
      '.anim__goal-complete-star-group'
    ) as HTMLElement[]

    tl.to(stars, {
      scale: 1,
      duration: 0.2,
      ease: 'sine.out',
      stagger: {
        each: 0.15,
      },
    })

    tl.to(stars[0], {
      y: -1,
      duration: 1.75,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
    })

    tl.to(
      stars[1],
      {
        y: 1,
        duration: 1.25,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      },
      '<'
    )

    return tl
  }

  const starBurst = () => {
    const tl = gsap.timeline({ delay: 1 })

    const stars = gsap.utils.toArray('.anim__starburst') as HTMLElement[]

    tl.set(stars, {
      scale: 'random(0.4, 1.4)',
      opacity: 1,
    })

    tl.to(stars, {
      duration: 1.25,
      opacity: 1,
      delay: 'random(0, 0.2)',
      physics2D: {
        velocity: 'random(400, 800)',
        angle: 'random(250, 300)',
        gravity: 'random(200, 600)',
      },
    })

    tl.to(stars, {
      scale: 0,
      duration: 0.4,
      opacity: 0,
      ease: 'sine.in',
    })

    return tl
  }

  useGSAP(
    () => {
      const tl = gsap.timeline({
        delay: motion?.delay || 0,
        scrollTrigger: {
          trigger: container.current,
          start: '125% bottom',
          once: true,
        },
      })

      tl.add(shimmer(), 'start')
      tl.add(starBurst(), '<start')
      tl.add(moveStars(), '<start')
      tl.add(moveStarGroups(), '<start')
    },
    { scope: container }
  )

  const shimmerColorLight =
    'linear-gradient( to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0))'
  const shimmerColorDark =
    'linear-gradient( to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0))'

  return (
    <div
      ref={container}
      className={css`
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        overflow: hidden;
      `}
    >
      <div
        className={cx(
          'anim__goal-complete',
          css`
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
          `
        )}
      >
        <div
          className={cx(
            'anim__goal-complete-bg',
            css`
              width: 100%;
              height: 100%;
              position: absolute;
              top: 0;
              left: 0;
              transform: translateY(-100%);
            `
          )}
        >
          <span
            className={css`
              display: block;
              width: 200%;
              aspect-ratio: 1 / 1;
              border-radius: 50%;
              background-color: #fac826;
              position: absolute;
              top: ${stretched ? '-250%' : '-100%'};
              left: ${stretched ? '65%' : '75%'};
              transform: translateX(-50%);
              filter: blur(80px);
              opacity: 0.75;
            `}
          />
        </div>
        <div
          className={cx(
            'anim__goal-complete-shimmer',
            css`
              width: 100%;
              height: 100%;
              position: absolute;
              top: 0;
              left: 0;
              transform: translateY(${stretched ? '275%' : '200%'});
            `
          )}
        >
          <span
            className={css`
              display: block;
              width: 100%;
              height: ${stretched ? '400%' : '200%'};
              background: ${theme.id.includes('dark')
                ? shimmerColorDark
                : shimmerColorLight};
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
            `}
          />
        </div>
        <div
          className={css`
            max-width: 170px;
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 50%;
            z-index: 1;
            transform: translateX(-50%);
          `}
        >
          <GoalCompleteStar
            className={css`
              width: 13px;
              position: absolute;
              left: 11px;
              top: 50%;
              transform: translateY(-50%) scale(0);
            `}
          />
          <GoalCompleteStar
            className={css`
              width: 11px;
              position: absolute;
              right: 20px;
              top: 26px;
              transform: scale(0);
            `}
          />
          <GoalCompleteStar
            className={css`
              width: 5px;
              position: absolute;
              left: 58%;
              bottom: 28%;
              transform: translateX(-50%) scale(0);
            `}
          />
          <GoalCompleteStarGroupLeft />
          <GoalCompleteStarGroupRight />
        </div>
        {Array.from({ length: 30 }).map((_, i) => (
          <GoalCompleteStar
            key={i}
            animClass="anim__starburst"
            className={css`
              width: 13px;
              height: 50px;
              position: absolute;
              left: 50%;
              bottom: -40px;
              transform: translateX(-50%);
            `}
          />
        ))}
      </div>
    </div>
  )
}
